// components/Calendar/Calendar.jsx
import React, { useState, useEffect } from "react";
import { useEvents } from "../../hooks/useEvents";
import "./Calendar.css";

const Calendar = () => {
  const { esdeveniments, loadingEsdeveniments } = useEvents();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [eventDays, setEventDays] = useState([]);
  const [selectedDay, setSelectedDay] = useState(null);

  // Funciones para la navegación del calendario
  const prevMonth = () => {
    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1)
    );
  };

  const nextMonth = () => {
    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1)
    );
  };

  // Obtener nombres de meses en catalán
  const monthNames = [
    "Gener",
    "Febrer",
    "Març",
    "Abril",
    "Maig",
    "Juny",
    "Juliol",
    "Agost",
    "Setembre",
    "Octubre",
    "Novembre",
    "Desembre",
  ];

  // Obtener nombres de días en catalán
  const dayNames = ["Dl", "Dt", "Dc", "Dj", "Dv", "Ds", "Dg"];

  // Extraer eventos del mes actual
  useEffect(() => {
    if (esdeveniments && esdeveniments.length > 0) {
      // Filtrar eventos del mes actual
      const currentYear = currentDate.getFullYear();
      const currentMonth = currentDate.getMonth();

      // Obtener los días que tienen eventos
      const days = esdeveniments
        .filter((event) => {
          const eventDate = new Date(event.data);
          return (
            eventDate.getFullYear() === currentYear &&
            eventDate.getMonth() === currentMonth
          );
        })
        .map((event) => {
          const eventDate = new Date(event.data);
          return {
            day: eventDate.getDate(),
            event: event,
          };
        });

      setEventDays(days);
    }
  }, [esdeveniments, currentDate]);

  // Generar la matriz del calendario
  const generateCalendarDays = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    // Primer día del mes
    const firstDayOfMonth = new Date(year, month, 1);
    // Último día del mes
    const lastDayOfMonth = new Date(year, month + 1, 0);

    // Día de la semana del primer día (0 = Domingo, 1 = Lunes, ..., 6 = Sábado)
    let firstDayWeekday = firstDayOfMonth.getDay();
    // Ajustar para que empiece en lunes (1) en lugar de domingo (0)
    firstDayWeekday = firstDayWeekday === 0 ? 6 : firstDayWeekday - 1;

    const totalDays = lastDayOfMonth.getDate();
    const weeks = Math.ceil((totalDays + firstDayWeekday) / 7);

    const calendarDays = [];
    let dayCount = 1;

    for (let i = 0; i < weeks; i++) {
      const week = [];
      for (let j = 0; j < 7; j++) {
        if ((i === 0 && j < firstDayWeekday) || dayCount > totalDays) {
          // Día vacío
          week.push(null);
        } else {
          // Comprobar si este día tiene eventos
          const hasEvent = eventDays.some(
            (eventDay) => eventDay.day === dayCount // eslint-disable-line no-loop-func
          );

          // Obtener eventos para este día
          const dayEvents = eventDays
            .filter((eventDay) => eventDay.day === dayCount) // eslint-disable-line no-loop-func
            .map((eventDay) => eventDay.event);

          week.push({
            day: dayCount,
            hasEvent,
            events: dayEvents,
          });

          dayCount++;
        }
      }
      calendarDays.push(week);
    }

    return calendarDays;
  };

  // Renderizar un día del calendario
  const renderDay = (dayData) => {
    if (!dayData) {
      return <div className="calendar-day empty"></div>;
    }

    const isToday =
      currentDate.getFullYear() === new Date().getFullYear() &&
      currentDate.getMonth() === new Date().getMonth() &&
      dayData.day === new Date().getDate();

    const isSelected =
      selectedDay &&
      selectedDay.day === dayData.day &&
      selectedDay.month === currentDate.getMonth() &&
      selectedDay.year === currentDate.getFullYear();

    const handleDayClick = () => {
      setSelectedDay({
        day: dayData.day,
        month: currentDate.getMonth(),
        year: currentDate.getFullYear(),
        events: dayData.events || [],
      });
    };

    return (
      <div
        className={`calendar-day ${isToday ? "today" : ""} ${
          dayData.hasEvent ? "has-event" : ""
        } ${isSelected ? "selected" : ""}`}
        title={
          dayData.hasEvent ? dayData.events.map((e) => e.titol).join(", ") : ""
        }
        onClick={handleDayClick}
      >
        <span className="day-number">{dayData.day}</span>
        {dayData.hasEvent && <span className="event-indicator"></span>}
      </div>
    );
  };

  // Función para formatear la fecha
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("ca-ES", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  // Cambio de mes resetea el día seleccionado
  const currentMonth = currentDate.getMonth();
  const currentYear = currentDate.getFullYear();
  useEffect(() => {
    setSelectedDay(null);
  }, [currentMonth, currentYear]);

  return (
    <div className="calendar-container">
      <div className="calendar-header">
        <div className="calendar-nav">
          <button onClick={prevMonth} className="nav-button">
            &lt;
          </button>
          <span className="current-month">
            {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
          </span>
          <button onClick={nextMonth} className="nav-button">
            &gt;
          </button>
        </div>
      </div>

      <div className="calendar-body">
        <div className="calendar-weekdays">
          {dayNames.map((day, index) => (
            <div key={index} className="weekday">
              {day}
            </div>
          ))}
        </div>

        <div className="calendar-days">
          {loadingEsdeveniments ? (
            <div className="loading">Carregant esdeveniments...</div>
          ) : (
            generateCalendarDays().map((week, weekIndex) => (
              <div key={weekIndex} className="calendar-week">
                {week.map((day, dayIndex) => (
                  <React.Fragment key={dayIndex}>
                    {renderDay(day)}
                  </React.Fragment>
                ))}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Eventos del día seleccionado */}
      {selectedDay && selectedDay.events && selectedDay.events.length > 0 && (
        <div className="selected-day-events">
          <h4>
            Esdeveniments del {selectedDay.day} de{" "}
            {monthNames[selectedDay.month]}
          </h4>
          <ul className="events-list">
            {selectedDay.events.map((event, index) => (
              <li key={index} className="event-item">
                <div className="event-date">{formatDate(event.data)}</div>
                <div className="event-title">{event.titol}</div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default Calendar;
